'use client';

import * as React from 'react';
import { ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from './utils';

// --- Radix primitives (for advanced multi-part usage) ---

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function TooltipRoot({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'origin-(--radix-tooltip-content-transform-origin) z-50 w-fit text-balance rounded-md bg-neutral-900 px-3 py-1.5 text-xs text-white shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        {...props}
      >
        {children}
        {/* The literal fill keeps the arrow glued to the tooltip surface: fill-neutral-900
            follows the text scale in dark mode and would detach as a light caret. */}
        <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-neutral-900 fill-[#171717]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

// --- Simple Tooltip (legacy-compatible single-component API) ---

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  className?: string;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  theme?: {
    target?: string;
  };
  /** Flowbite-compatible props — ignored, kept for call-site compatibility. */
  style?: 'dark' | 'light' | 'auto';
  animation?: string;
  id?: string;
}

const Tooltip = ({ children, content, className, placement = 'top', theme }: TooltipProps) => {
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <span className={theme?.target}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side={placement} className={className}>
        {content}
      </TooltipContent>
    </TooltipRoot>
  );
};

export { Tooltip, TooltipRoot, TooltipTrigger, TooltipContent, TooltipProvider };
